"use client";
// React Required
import React, { useCallback, useEffect, useState } from "react";

// Internal Lib
import { Button, showToast } from "@int-traderjoes/ui-components-library";

// Contexts
import { useFormContext } from "@/context/FormContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { DefaultManufacturingFacilityInformationFormData } from "@/context/data";
import { useLanguageContext } from "@/context/LanguageContext";
import { DropdownData } from "@/types/vendor.types";

// Components
import ManufacturingFacilityInformationForm from "@/components/ManufacturingFacilityInformationForm";

// Services
import VendorService from "@/services/vendorService";
import VendorGuestService from "@/services/vendorGuestService";
import vendorManufacturingFacilityAssociationService from "@/services/vendorManufacturingFacilityAssociationService";
import { handleApiError } from "@/hooks/useApiErrorHandler";
import { hasPersistedManufacturingFacilityId } from "@/utils/vendor/mapManufacturingFacilityForApiPayload";

// Types
import type { ExistingSite } from "@/types/VendorService.types";

// MUI
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

interface AddManufacturingFacilityProps {
  isVendor?: boolean;
  activeSections: Set<string>;
  handleToggle: (sectionName: string) => void;
  formUpdated: () => void;
  allSections?: React.RefObject<Set<string> | null>;
  isFormDisabled?: boolean;
  dropdownData: DropdownData;
  /** When true (from vendorAssessment.hasExistingVendor), show "Is This New or Existing Site?" in each site form */
  hasExistingVendor?: boolean | null;
  /** Current vendor id - required to fetch existing sites when "Existing" is selected (TJ flow) */
  vendorId?: string | number | null;
  /** Guest vendor-form: assessment id for getExistingFacilities */
  guestVendorAssessmentId?: string | null;
  /** TJ vendor assessment: id for Pimcore associate API when removing a persisted facility row */
  vendorAssessmentId?: string | null;
  /** When set, each facility row is wrapped in a Box with id `${prefix}${sectionKey}` for scroll-into-view (e.g. guest VendorForm). */
  sectionScrollIdPrefix?: string;
  /** Optional zoom scale styles for the "+ ADD SITE" button container (e.g. Active Projects useZoomScale). */
  addSiteButtonSx?: SxProps<Theme>;
}

const AddManufacturingFacility: React.FC<AddManufacturingFacilityProps> = ({
  isVendor,
  activeSections,
  handleToggle,
  formUpdated,
  allSections,
  isFormDisabled,
  dropdownData,
  hasExistingVendor,
  vendorId,
  guestVendorAssessmentId,
  vendorAssessmentId,
  sectionScrollIdPrefix,
  addSiteButtonSx,
}) => {
  const {
    manufacturingFacilityInformationForms,
    setManufacturingFacilityInformationForms,
  } = useFormContext();
  const { setIsLoading } = useGlobalStateContext();
  const { languageText } = useLanguageContext();
  const buttonTexts = languageText?.BUTTONS;
  const toastMessageTexts = languageText?.MESSAGES?.TOAST_MESSAGES;

  /** One shared catalogue for all facility rows — avoids duplicate getExistingSites / guest facilities calls per row. */
  const [sharedExistingSitesCatalog, setSharedExistingSitesCatalog] = useState<ExistingSite[]>([]);
  const [sharedExistingSitesLoading, setSharedExistingSitesLoading] = useState(() => {
    const hasVid = vendorId != null && String(vendorId).trim() !== "";
    const aid =
      guestVendorAssessmentId != null && String(guestVendorAssessmentId).trim() !== "";
    return hasVid || aid;
  });

  const fetchSharedExistingSites = useCallback(async () => {
    if (!hasExistingVendor) {
      setSharedExistingSitesCatalog([]);
      setSharedExistingSitesLoading(false);
      return;
    }
    const isGuestFlow = isVendor === true;
    if (isGuestFlow) {
      const aid = guestVendorAssessmentId?.trim() ?? "";
      if (aid === "") {
        setSharedExistingSitesCatalog([]);
        setSharedExistingSitesLoading(false);
        return;
      }
      setSharedExistingSitesLoading(true);
      try {
        const sites = await VendorGuestService.getGuestExistingFacilities(aid);
        setSharedExistingSitesCatalog(Array.isArray(sites) ? sites : []);
      } catch (err) {
        handleApiError(
          err,
          toastMessageTexts?.FETCH_VENDOR_INFORMATION ?? "Failed to fetch existing sites"
        );
        setSharedExistingSitesCatalog([]);
      } finally {
        setSharedExistingSitesLoading(false);
      }
      return;
    }
    if (vendorId == null || String(vendorId).trim() === "") {
      setSharedExistingSitesCatalog([]);
      setSharedExistingSitesLoading(false);
      return;
    }
    setSharedExistingSitesLoading(true);
    try {
      const sites = await VendorService.getExistingSites(vendorId);
      setSharedExistingSitesCatalog(Array.isArray(sites) ? sites : []);
    } catch (err) {
      handleApiError(
        err,
        toastMessageTexts?.FETCH_VENDOR_INFORMATION ?? "Failed to fetch existing sites"
      );
      setSharedExistingSitesCatalog([]);
    } finally {
      setSharedExistingSitesLoading(false);
    }
  }, [hasExistingVendor, isVendor, guestVendorAssessmentId, vendorId]);

  useEffect(() => {
    void fetchSharedExistingSites();
  }, [fetchSharedExistingSites]);

  const handleAddNewFacility = () => {
    const newIndex = manufacturingFacilityInformationForms.length;
    const newSectionKey = `manufacturingFacilityInfoForm-${newIndex}`;

    // Add the new section to allSections ref
    if (allSections?.current) {
      allSections?.current?.add(newSectionKey);
    }

    // Add the new form data
    setManufacturingFacilityInformationForms((prevForms) => [
      ...prevForms,
      DefaultManufacturingFacilityInformationFormData,
    ]);

    handleToggle(newSectionKey); // then expand it

    // Trigger form update
    formUpdated();
  };

  const removeFacilityRowAtIndex = useCallback(
    (index: number) => {
      const sectionKey = `manufacturingFacilityInfoForm-${index}`;
      if (allSections?.current && allSections?.current?.has(sectionKey)) {
        allSections.current.delete(sectionKey);
      }
      setManufacturingFacilityInformationForms((prev) => prev.filter((_, i) => i !== index));
      formUpdated();
    },
    [allSections, formUpdated, setManufacturingFacilityInformationForms]
  );

  const handlePersistedSiteDelete = useCallback(
    async (index: number) => {
      const assessmentId = String(vendorAssessmentId ?? guestVendorAssessmentId ?? "").trim();
      if (!assessmentId) {
        showToast({
          type: "error",
          message:
            toastMessageTexts?.MISSING_ASSESSMENT_FOR_SITE_DELETE ??
            "Cannot remove site: missing vendor assessment id.",
        });
        return;
      }
      const nextForms = manufacturingFacilityInformationForms.filter((_, i) => i !== index);
      const facilityIds = nextForms
        .filter((f) => hasPersistedManufacturingFacilityId((f ?? {}) as Record<string, unknown>))
        .map((f) => String((f as Record<string, unknown>).id));

      const isGuestVendorFlow = isVendor === true;
      setIsLoading(true);
      try {
        if (isGuestVendorFlow) {
          await vendorManufacturingFacilityAssociationService.associateFacilitiesWithVendorAssessmentAsGuest({
            facilityIds,
            assessmentId,
          });
        } else {
          await vendorManufacturingFacilityAssociationService.associateFacilitiesWithVendorAssessment({
            facilityIds,
            assessmentId,
          });
        }
        removeFacilityRowAtIndex(index);
        showToast({
          type: "success",
          message:
            toastMessageTexts?.REMOVE_SITE_SUCCESS ?? "Site removed successfully.",
        });
      } catch (err) {
        handleApiError(
          err,
          toastMessageTexts?.FAILED_TO_REMOVE_SITE ?? "Failed to remove site. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      guestVendorAssessmentId,
      isVendor,
      manufacturingFacilityInformationForms,
      removeFacilityRowAtIndex,
      setIsLoading,
      toastMessageTexts?.FAILED_TO_REMOVE_SITE,
      toastMessageTexts?.MISSING_ASSESSMENT_FOR_SITE_DELETE,
      toastMessageTexts?.REMOVE_SITE_SUCCESS,
      vendorAssessmentId,
    ]
  );

  const handleDelete = useCallback(
    (index: number) => {
      const row = manufacturingFacilityInformationForms[index];
      if (!hasPersistedManufacturingFacilityId((row ?? {}) as Record<string, unknown>)) {
        removeFacilityRowAtIndex(index);
      }
    },
    [manufacturingFacilityInformationForms, removeFacilityRowAtIndex]
  );

  // Check if any facility section is currently expanded
  const isAnyFacilityExpanded = manufacturingFacilityInformationForms.some(
    (_, index) => activeSections.has(`manufacturingFacilityInfoForm-${index}`)
  );

  return (
    <>
      {manufacturingFacilityInformationForms.map((manufacturingFacilityInfoForm, index) => {
        const sectionKey = `manufacturingFacilityInfoForm-${index}`;
        const facilityForm = (
          <ManufacturingFacilityInformationForm
            manufacturingFacilityInformationFormData={manufacturingFacilityInfoForm}
            maufactureFacilityInfoKey={index}
            onDelete={() => handleDelete(index)}
            onPersistedDeleteConfirm={() => handlePersistedSiteDelete(index)}
            isVendor={isVendor}
            isExpanded={activeSections.has(sectionKey)}
            onToggle={() => handleToggle(sectionKey)}
            isFormDisabled={isFormDisabled}
            dropdownData={dropdownData}
            hasExistingVendor={hasExistingVendor}
            vendorId={vendorId}
            guestVendorAssessmentId={guestVendorAssessmentId}
            sharedExistingSitesCatalog={sharedExistingSitesCatalog}
            sharedExistingSitesLoading={sharedExistingSitesLoading}
          />
        );
        if (sectionScrollIdPrefix) {
          return (
            <Box key={sectionKey} id={`${sectionScrollIdPrefix}${sectionKey}`}>
              {facilityForm}
            </Box>
          );
        }
        return (
          <React.Fragment key={sectionKey}>
            {facilityForm}
          </React.Fragment>
        );
      })}

      {/* Show Add Facility button only when at least one facility is expanded */}
      {isAnyFacilityExpanded && !isFormDisabled && (
        <Box className="md:w-[180px]" sx={addSiteButtonSx}>
          <Button
              size="medium"
              text={buttonTexts?.ADD_FACILITY_WITH_PLUS || "+ ADD SITE"}
              primary
              onClick={handleAddNewFacility}
              className="add-new-facility-button"
            />
          </Box>
      )}
    </>
  );
};

export default AddManufacturingFacility;
